// ─────────────────────────────────────────────────────────────────────────────
// Competitor-show lifecycle stage — single source of truth for the New → Awaiting
// Review → In Review → Completed pipeline. Ported from ShowsPage so the combined
// Portfolio page and the tracker stay consistent.
// ─────────────────────────────────────────────────────────────────────────────

export const PIPELINE_STEPS = ["New", "Awaiting Review", "In Review", "Completed"];

// enrichStatus (derived from latest_job_status + company count) → stage tuple
const PH_MAP = {
  "Added":                  ["New",             "enrichment pending", "#9a5b00", "#fef4e6", "#F18F18"],
  "Waiting for Enrichment": ["New",             "queued",             "#9a5b00", "#fef4e6", "#F18F18"],
  "Enrichment Running":     ["New",             "enriching…",         "#E8431A", "#FCEBE5", "#E8431A"],
  "Enriched":               ["Awaiting Review", "assign a reviewer",  "#960659", "#F6E6EF", "#960659"],
  "Ready for Assignment":   ["Awaiting Review", "assign a reviewer",  "#960659", "#F6E6EF", "#960659"],
  "Assigned":               ["In Review",       "review in progress", "#5F1156", "#F5EEF2", "#960659"],
  "In Review":              ["In Review",       "review in progress", "#5F1156", "#F5EEF2", "#960659"],
  "Review Completed":       ["Completed",       "review completed",   "#166534", "#f0fdf4", "#16a34a"],
};

const STEP_BY_KEY = { new: 1, awaiting: 2, review: 3, done: 4 };
const KEY_BY_LABEL = { "New": "new", "Awaiting Review": "awaiting", "In Review": "review", "Completed": "done" };

// Derive a show's lifecycle stage from its enrichment status + the sales/marketing
// assignment progress. Returns { key, label, sub, c, bg, dot, step }.
export function deriveStage(show, salesAsgn, mktAsgn) {
  const es          = show.enrichStatus;
  const reviewed    = salesAsgn?.reviewed_count || 0;
  const reviewTotal = salesAsgn?.total_companies || show.companiesFound || 0;
  const mRev        = mktAsgn?.reviewed_count || 0;
  const mTot        = mktAsgn?.total_companies || show.companiesFound || 0;
  const anyProgress = reviewed > 0 || mRev > 0;
  const salesDone   = salesAsgn && reviewTotal > 0 && reviewed >= reviewTotal;
  const mktDone     = mktAsgn && mTot > 0 && mRev >= mTot;
  const allDone     = anyProgress && (!salesAsgn || salesDone) && (!mktAsgn || mktDone);
  const hasAsgn     = !!(salesAsgn || mktAsgn);

  let tuple;
  if (!anyProgress && hasAsgn && (es === "Enriched" || es === "Ready for Assignment")) {
    tuple = ["Awaiting Review", "assigned, not started", "#960659", "#F6E6EF", "#960659"];
  } else if (!anyProgress || es !== "Enriched") {
    tuple = PH_MAP[es] || ["New", "enrichment pending", "#9a5b00", "#fef4e6", "#F18F18"];
  } else if (allDone) {
    tuple = ["Completed", "review completed", "#166534", "#f0fdf4", "#16a34a"];
  } else {
    tuple = ["In Review", "review in progress", "#5F1156", "#F5EEF2", "#960659"];
  }

  const [label, sub, c, bg, dot] = tuple;
  const key = KEY_BY_LABEL[label] || "new";
  return { key, label, sub, c, bg, dot, step: STEP_BY_KEY[key] || 1 };
}

// Map a /shows list row to the minimal shape the Portfolio page + deriveStage need.
export function mapShowRow(s) {
  const js  = s.latest_job_status;
  const cnt = s.total_companies || 0;
  const enrichStatus =
    js === "enriching"                 ? "Enrichment Running" :
    js === "queued"                    ? "Waiting for Enrichment" :
    (js === "completed" || cnt > 0)    ? "Enriched" :
                                         "Added";
  return {
    id:             s.show_id,
    event:          s.ite_event_id,
    name:           s.name,
    year:           s.year,
    location:       s.location || "",
    startDate:      s.start_date || null,
    endDate:        s.end_date || null,
    website:        s.website || "",
    companiesFound: cnt,
    enrichStatus,
  };
}
